import api from "./api";
import { fetchUserById } from "./usersService";
import NotificationService from "./NotificationService";

const UsertoProjectService = {

    createUserToProject(data) {
        return api.post(`/api/v1/userToProject`, data);
    },

    getAllUserToProject() {
        return api.get(`/api/v1/userToProject`);
    },

    updateUserToProjectStatus(UserToProjectId, statusId) {
        return api.put(`/api/v1/userToProject/${UserToProjectId}/statusUpdate/${statusId}`);
    },

    updateProject(userToProjectId, data) {
        return api.put(`/api/v1/userToProject/${userToProjectId}`, data);
    },

    deleteProject(id) {
        return api.delete(`/api/v1/userToProject/${id}`);
    },

    getUserToProjectById(userToProjectId) {
        return api.get(`/api/v1/userToProject/${userToProjectId}`);
    },

    getUsersByProjectId(projectId) {
        return api.get(`/api/v1/userToProject/byProjectId/${projectId}`);
    },

    /**
     * Assigns projects to a user.
     * - isNewUser=true  → skip GET, POST directly (new user has no mapping yet)
     * - isNewUser=false → GET all, try to find mapping by multiple field patterns, then PUT or POST
     * - 409 fallback    → if POST still conflicts, find mapping and PUT
     */
    async assignProjectToUser(userId, newProjectIds, notificationData = null, isNewUser = false) {
        if (!userId || !newProjectIds?.length) return;

        const projectIdNums = newProjectIds.map(Number);
        console.log("[assignProjectToUser] Input:", { userId, projectIdNums, isNewUser });

        // Helper: try every known field pattern to match userId
        const findMapping = (mappings) => mappings.find((m) =>
            String(m.userId) === String(userId) ||
            String(m.user?.id) === String(userId) ||
            String(m.mappingUserId) === String(userId)
        );

        try {
            // ── FAST PATH: brand new user, no mapping exists ──
            if (isNewUser) {
                console.log("[assignProjectToUser] New user → POST");
                const createRes = await api.post(`/api/v1/userToProject`, {
                    userId,
                    projectIds: projectIdNums,
                });
                console.log("[assignProjectToUser] CREATE SUCCESS", createRes.data);
                return { success: true, action: "created", data: createRes.data };
            }

            // ── EXISTING USER: GET all mappings, find this user's record ──
            console.log("[assignProjectToUser] Existing user → GET all to find mapping");
            const res = await UsertoProjectService.getAllUserToProject();
            let allMappings = res.data || [];
            if (!Array.isArray(allMappings)) allMappings = [allMappings];

            // Log first item so we can see the actual structure in the console
            if (allMappings.length > 0) {
                console.log("[assignProjectToUser] Sample mapping keys:", Object.keys(allMappings[0]));
            }

            const userMapping = findMapping(allMappings);

            if (userMapping) {
                const mappingId = userMapping.userToProjectId || userMapping.id;
                console.log("[assignProjectToUser] Mapping found → PUT:", mappingId);
                const updateRes = await api.put(`/api/v1/userToProject/${mappingId}`, {
                    userId,
                    projectIds: projectIdNums,
                });
                console.log("[assignProjectToUser] UPDATE SUCCESS", updateRes.data);
                return { success: true, action: "updated", data: updateRes.data };
            }

            // No mapping found → try POST
            console.log("[assignProjectToUser] No mapping found → POST");
            const createRes = await api.post(`/api/v1/userToProject`, {
                userId,
                projectIds: projectIdNums,
            });
            console.log("[assignProjectToUser] CREATE SUCCESS", createRes.data);
            return { success: true, action: "created", data: createRes.data };

        } catch (err) {
            // ── 409 FALLBACK: POST conflicted, mapping exists but find() missed it ──
            if (err.response?.status === 409) {
                console.warn("[assignProjectToUser] 409 → fetching all mappings for PUT fallback");
                try {
                    const fallbackRes = await UsertoProjectService.getAllUserToProject();
                    let fallbackMappings = fallbackRes.data || [];
                    if (!Array.isArray(fallbackMappings)) fallbackMappings = [fallbackMappings];

                    console.log("[assignProjectToUser] Fallback mappings sample:", fallbackMappings[0]);

                    const fallbackMapping = findMapping(fallbackMappings);
                    if (fallbackMapping) {
                        const mappingId = fallbackMapping.userToProjectId || fallbackMapping.id;
                        console.log("[assignProjectToUser] Fallback PUT:", mappingId);
                        const updateRes = await api.put(`/api/v1/userToProject/${mappingId}`, {
                            userId,
                            projectIds: projectIdNums,
                        });
                        console.log("[assignProjectToUser] FALLBACK UPDATE SUCCESS", updateRes.data);
                        return { success: true, action: "updated", data: updateRes.data };
                    }
                    console.error("[assignProjectToUser] 409 but no mapping found — check console for response structure");
                } catch (innerErr) {
                    console.error("[assignProjectToUser] Fallback PUT failed:", innerErr.response?.data || innerErr.message);
                    throw innerErr;
                }
            }
            console.error("[assignProjectToUser] Error:", err.response?.data || err.message);
            throw err;
        }
    }
};

export default UsertoProjectService;